import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, auth, formatApiErrorDetail } from "../lib/api";
import { LOGO_ICON_URL } from "../lib/branding";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  Car,
  MessageSquare,
  Calendar,
  LogOut,
  RefreshCcw,
  Trash2,
  CheckCircle2,
  Circle,
  XCircle,
  ClipboardList,
  Loader2,
  User,
} from "lucide-react";

const STATUS_LABELS = {
  pending: "Neu",
  confirmed: "Bestätigt",
  done: "Erledigt",
  rejected: "Abgelehnt",
};

const STATUS_STYLES = {
  pending: "gold-bg text-black",
  confirmed: "bg-blue-500 text-white",
  done: "bg-green-600 text-white",
  rejected: "bg-zinc-700 text-zinc-300",
};

const STATUS_ICON = {
  pending: Circle,
  confirmed: CheckCircle2,
  done: CheckCircle2,
  rejected: XCircle,
};

const FILTERS = [
  { k: "all", label: "Alle" },
  { k: "pending", label: "Neu" },
  { k: "confirmed", label: "Bestätigt" },
  { k: "done", label: "Erledigt" },
  { k: "rejected", label: "Abgelehnt" },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function formatPreferred(s) {
  if (!s) return "—";
  // preferred_date is datetime-local string: "2025-12-15T10:00"
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, done: 0, rejected: 0, total: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState({}); // {id: bool}
  const [admin, setAdmin] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get("/admin/appointments", {
          params: filter === "all" ? {} : { status: filter },
        }),
        api.get("/admin/stats"),
      ]);
      setItems(listRes.data || []);
      setStats(statsRes.data || {});
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Laden.");
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setAdmin(data);
      } catch {
        /* handled by interceptor */
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    auth.clearToken();
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    setWorking((w) => ({ ...w, [id]: true }));
    try {
      const { data } = await api.patch(`/admin/appointments/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      // refresh stats
      const s = await api.get("/admin/stats");
      setStats(s.data || {});
      toast.success(`Status: ${STATUS_LABELS[status]}`);
      if (filter !== "all" && filter !== status) {
        // item now filtered away
        setItems((prev) => prev.filter((it) => it.id !== id));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Aktualisieren.");
    } finally {
      setWorking((w) => ({ ...w, [id]: false }));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Diese Anfrage wirklich endgültig löschen?")) return;
    setWorking((w) => ({ ...w, [id]: true }));
    try {
      await api.delete(`/admin/appointments/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      const s = await api.get("/admin/stats");
      setStats(s.data || {});
      toast.success("Anfrage gelöscht.");
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Löschen.");
    } finally {
      setWorking((w) => ({ ...w, [id]: false }));
    }
  };

  const counts = useMemo(
    () => [
      { k: "pending", label: "Neu", value: stats.pending || 0, accent: "gold-bg text-black" },
      { k: "confirmed", label: "Bestätigt", value: stats.confirmed || 0, accent: "bg-blue-500 text-white" },
      { k: "done", label: "Erledigt", value: stats.done || 0, accent: "bg-green-600 text-white" },
      { k: "rejected", label: "Abgelehnt", value: stats.rejected || 0, accent: "bg-zinc-700 text-zinc-300" },
    ],
    [stats]
  );

  return (
    <div className="min-h-screen bg-[#09090b] text-white font-[IBM_Plex_Sans]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/90 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 shrink-0 inline-block logo-blend">
              <img
                src={LOGO_ICON_URL}
                alt="Autohaus Barwinski Logo"
                className="w-full h-full object-cover"
              />
            </span>
            <div className="leading-tight">
              <div className="font-display text-sm md:text-base uppercase tracking-tight">
                Admin-Bereich
              </div>
              <div className="text-[10px] text-zinc-500 tracking-[0.25em] uppercase">
                Autohaus Barwinski
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {admin && (
              <span className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-400">
                <User size={14} />
                {admin.email}
              </span>
            )}
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#E0B33D] hover:gold-text px-3 py-2 text-[11px] tracking-[0.25em] uppercase transition-colors"
            >
              <RefreshCcw size={14} /> Aktualisieren
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-3 py-2 text-[11px] tracking-[0.25em] uppercase"
            >
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-14">
        {/* Headline */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase gold-text">
              — Übersicht
            </div>
            <h1 className="font-display mt-3 text-4xl md:text-5xl uppercase tracking-tight leading-[0.95]">
              Ihre Terminanfragen
            </h1>
            <p className="mt-2 text-sm text-zinc-400">
              Klicken Sie auf eine Anfrage, um die Details zu sehen. Mit den
              farbigen Knöpfen unten ändern Sie den Status.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-8">
          {counts.map((c) => (
            <button
              key={c.k}
              onClick={() => setFilter(c.k)}
              data-testid={`stat-${c.k}`}
              className={`group text-left bg-[#0f0f12] hover:bg-[#18181b] p-5 md:p-6 transition-colors ${
                filter === c.k ? "ring-1 ring-[#E0B33D]" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase ${c.accent}`}
                >
                  {c.label}
                </span>
              </div>
              <div className="mt-4 font-display text-4xl md:text-5xl">
                {c.value}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">
                Anfragen
              </div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="filter-bar">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-4 py-2 text-[11px] tracking-[0.25em] uppercase border transition-colors ${
                filter === f.k
                  ? "gold-bg text-black border-[#E0B33D]"
                  : "border-zinc-700 text-zinc-300 hover:border-[#E0B33D] hover:gold-text"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 size={20} className="animate-spin mr-2" /> Lade …
          </div>
        ) : items.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div className="grid gap-4" data-testid="appointment-list">
            {items.map((it) => (
              <AppointmentRow
                key={it.id}
                appt={it}
                onOpen={() => setSelected(it)}
                onStatus={updateStatus}
                onDelete={remove}
                busy={!!working[it.id]}
              />
            ))}
          </div>
        )}
      </main>

      {selected && (
        <DetailModal
          appt={selected}
          onClose={() => setSelected(null)}
          onStatus={async (s) => {
            await updateStatus(selected.id, s);
            setSelected((cur) => (cur ? { ...cur, status: s } : cur));
          }}
          onDelete={async () => {
            await remove(selected.id);
          }}
          busy={!!working[selected.id]}
        />
      )}
    </div>
  );
}

function EmptyState({ filter }) {
  const msg =
    filter === "all"
      ? "Es liegen noch keine Terminanfragen vor."
      : `Keine Anfragen mit Status „${STATUS_LABELS[filter] || filter}".`;
  return (
    <div
      data-testid="empty-state"
      className="border border-dashed border-zinc-800 bg-[#0f0f12] text-center py-20 px-6"
    >
      <ClipboardList size={36} className="mx-auto text-zinc-600" />
      <h3 className="font-display mt-5 text-2xl uppercase tracking-tight">
        Alles ruhig
      </h3>
      <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">{msg}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const Icon = STATUS_ICON[status] || Circle;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase ${STATUS_STYLES[status]}`}
    >
      <Icon size={12} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function AppointmentRow({ appt, onOpen, onStatus, onDelete, busy }) {
  return (
    <div
      data-testid={`appointment-row-${appt.id}`}
      className="bg-[#0f0f12] border border-zinc-800 hover:border-[#E0B33D]/60 transition-colors"
    >
      <button
        onClick={onOpen}
        className="w-full text-left p-5 md:p-6"
        data-testid={`open-${appt.id}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight">
                {appt.name}
              </h3>
              <StatusBadge status={appt.status} />
            </div>
            <div className="mt-1 text-sm text-zinc-400 flex items-center gap-2 flex-wrap">
              <span className="uppercase tracking-wider text-[11px] gold-text">
                {appt.service}
              </span>
              <span className="text-zinc-600">·</span>
              <Calendar size={13} className="text-zinc-500" />
              <span>{formatPreferred(appt.preferred_date)}</span>
            </div>
          </div>
          <div className="text-right text-[10px] text-zinc-500 tracking-[0.25em] uppercase">
            Eingegangen
            <div className="text-xs text-zinc-400 tracking-normal mt-0.5">
              {formatDate(appt.created_at)}
            </div>
          </div>
        </div>
      </button>

      <div className="border-t border-zinc-800 p-4 md:p-5 flex flex-wrap items-center gap-2">
        <a
          href={`tel:${appt.phone}`}
          data-testid={`call-${appt.id}`}
          className="inline-flex items-center gap-2 gold-bg text-black font-semibold px-4 py-2.5 text-xs tracking-[0.2em] uppercase"
        >
          <Phone size={14} /> Anrufen
        </a>
        <a
          href={`mailto:${appt.email}`}
          data-testid={`mail-${appt.id}`}
          className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#E0B33D] hover:gold-text text-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase"
        >
          <Mail size={14} /> E-Mail
        </a>

        <div className="h-6 w-px bg-zinc-800 mx-1" />

        <StatusButtons
          status={appt.status}
          onStatus={(s) => onStatus(appt.id, s)}
          busy={busy}
        />

        <div className="ml-auto">
          <button
            onClick={() => onDelete(appt.id)}
            data-testid={`delete-${appt.id}`}
            disabled={busy}
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-red-400 px-3 py-2 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
            title="Anfrage löschen"
          >
            <Trash2 size={14} /> Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusButtons({ status, onStatus, busy }) {
  const btn = (target, label, cls, testid) => (
    <button
      key={target}
      disabled={busy || status === target}
      onClick={() => onStatus(target)}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : null}
      {label}
    </button>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {btn(
        "confirmed",
        "Bestätigen",
        "bg-blue-600 hover:bg-blue-500 text-white",
        "status-confirm"
      )}
      {btn(
        "done",
        "Erledigt",
        "bg-green-700 hover:bg-green-600 text-white",
        "status-done"
      )}
      {btn(
        "rejected",
        "Ablehnen",
        "border border-zinc-700 hover:border-red-500 hover:text-red-400 text-zinc-300",
        "status-reject"
      )}
    </div>
  );
}

function DetailModal({ appt, onClose, onStatus, onDelete, busy }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center px-4 py-10"
      onClick={onClose}
      data-testid="detail-overlay"
    >
      <div
        className="bg-[#0f0f12] border border-zinc-800 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="detail-modal"
      >
        <div className="p-6 md:p-8 border-b border-zinc-800 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={appt.status} />
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                Anfrage
              </span>
            </div>
            <h2 className="font-display mt-3 text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]">
              {appt.name}
            </h2>
            <div className="mt-1 text-sm text-zinc-400">
              {appt.service} · {formatPreferred(appt.preferred_date)}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="detail-close"
            className="text-zinc-400 hover:text-white px-2 py-1"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-4 text-sm">
          <DetailRow icon={Phone} label="Telefon" value={appt.phone} href={`tel:${appt.phone}`} />
          <DetailRow icon={Mail} label="E-Mail" value={appt.email} href={`mailto:${appt.email}`} />
          <DetailRow icon={Car} label="Fahrzeug" value={appt.vehicle || "—"} />
          <DetailRow icon={Calendar} label="Eingegangen" value={formatDate(appt.created_at)} />
          <DetailRow
            icon={MessageSquare}
            label="Nachricht"
            value={appt.message || "—"}
            multiline
          />
          {appt.vehicle_registration ? (
            <div className="flex items-start gap-3" data-testid="detail-vehicle-reg">
              <span className="w-9 h-9 gold-bg text-black grid place-items-center flex-shrink-0">
                <Car size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  Fahrzeugschein-Foto
                </div>
                <a
                  href={appt.vehicle_registration}
                  target="_blank"
                  rel="noreferrer"
                  className="block mt-2"
                >
                  <img
                    src={appt.vehicle_registration}
                    alt={appt.vehicle_registration_name || "Fahrzeugschein"}
                    className="max-w-full max-h-64 border border-zinc-700 hover:border-[#E0B33D] transition-colors"
                  />
                </a>
                <div className="mt-1 text-[11px] text-zinc-500">
                  {appt.vehicle_registration_name || "Anhang"} · zum Vergrößern klicken
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="p-5 md:p-6 border-t border-zinc-800 flex flex-wrap items-center gap-2">
          <a
            href={`tel:${appt.phone}`}
            data-testid="detail-call"
            className="inline-flex items-center gap-2 gold-bg text-black font-semibold px-4 py-2.5 text-xs tracking-[0.25em] uppercase"
          >
            <Phone size={14} /> Jetzt anrufen
          </a>
          <a
            href={`mailto:${appt.email}`}
            data-testid="detail-email"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#E0B33D] hover:gold-text px-4 py-2.5 text-xs tracking-[0.25em] uppercase"
          >
            <Mail size={14} /> E-Mail schreiben
          </a>

          <div className="h-6 w-px bg-zinc-800 mx-1" />

          <StatusButtons status={appt.status} onStatus={onStatus} busy={busy} />

          <div className="ml-auto">
            <button
              onClick={onDelete}
              disabled={busy}
              data-testid="detail-delete"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-red-400 px-3 py-2 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
            >
              <Trash2 size={14} /> Löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, href, multiline }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 gold-bg text-black grid place-items-center flex-shrink-0">
        <Icon size={16} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          {label}
        </div>
        {href ? (
          <a
            href={href}
            className="mt-0.5 block text-white hover:gold-text break-words"
          >
            {value}
          </a>
        ) : (
          <div
            className={`mt-0.5 text-white break-words ${multiline ? "whitespace-pre-wrap" : ""}`}
          >
            {value}
          </div>
        )}
      </div>
    </div>
  );
}
